/**
 * 주담 브랜드 색상 팔레트 (Design Tokens 1.0)
 * Source: shared/judam-design-tokens.ts
 */

const brand = {
  primary: '#1F6D64',
  primaryDark: '#185851',
  primarySoft: '#E7F2F0',
  /**
   * App chrome (상단바·모달 헤더·PWA 상태바) 전용 딥 그린.
   * 크롬을 가라앉혀 primary CTA가 앞으로 나오게 하는 것이 목적 — 버튼에는 쓰지 않는다.
   */
  chrome: '#0A302C',
  ink: '#24302E',
  copper: '#A85E38',
  copperSoft: '#F5E8DF',
} as const;

const surface = {
  canvas: '#F5F7F6',
  default: '#FFFFFF',
  subtle: '#EFF3F1',
  raised: '#FFFFFF',
  selected: '#E7F2F0',
  warm: '#F7F3EC',
} as const;

const textTokens = {
  primary: '#24302E',
  secondary: '#65716D',
  muted: '#8B9591',
  disabled: '#AEB6B2',
  inverse: '#FFFFFF',
} as const;

const borderTokens = {
  default: '#DDE4E1',
  subtle: '#EBEFED',
  strong: '#C7D0CC',
  focus: '#1F6D64',
  error: '#B84A45',
} as const;

const semantic = {
  success: '#4F7A3A',
  successSoft: '#EDF4E9',
  warning: '#A66A1F',
  warningSoft: '#F8F0E3',
  error: '#B84A45',
  errorSoft: '#F8E9E7',
  info: '#3D7188',
  infoSoft: '#E8F0F3',
} as const;

const radius = {
  /** 버튼·인풋·필터·셀렉트 — 짧은 컨트롤에서 알약처럼 보이지 않도록 */
  control: 10,
  /** 카드·패널·테이블·시트 */
  card: 16,
  panel: 16,
  sheet: 16,
  /** 칩·배지 등만 */
  pill: 999,
} as const;

/** MUI sx용 px 문자열. 숫자는 shape.borderRadius 배수가 되므로 반드시 이쪽을 쓴다. */
const radiusPx = {
  control: `${radius.control}px`,
  card: `${radius.card}px`,
  panel: `${radius.panel}px`,
  sheet: `${radius.sheet}px`,
  pill: `${radius.pill}px`,
} as const;

const shadow = {
  low: '0 1px 2px rgba(25,35,45,0.05), 0 4px 12px rgba(25,35,45,0.04)',
  high: '0 12px 32px rgba(25,35,45,0.14)',
} as const;

const spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

const motion = {
  fast: '140ms',
  standard: '200ms',
  sheet: '320ms',
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
} as const;

const colors = {
  brand,
  surface,
  borderTokens,
  semantic,
  radius,
  radiusPx,
  shadow,
  spacing,
  motion,

  // MUI / legacy shape — maps onto Design Tokens 1.0
  primary: {
    main: brand.primary,
    light: brand.primarySoft,
    dark: brand.primaryDark,
    contrastText: textTokens.inverse,
  },

  /** @deprecated use brand.primary — kept for gradual migration */
  accent1: {
    main: brand.primary,
    light: '#438E83',
    dark: brand.primaryDark,
    contrastText: textTokens.inverse,
  },

  /** Copper brand accent point */
  accent2: {
    main: brand.copper,
    light: '#C47A52',
    dark: '#8A4A2A',
    contrastText: textTokens.inverse,
  },

  copper: {
    main: brand.copper,
    light: '#C47A52',
    dark: '#8A4A2A',
    soft: brand.copperSoft,
    contrastText: textTokens.inverse,
  },

  ink: {
    main: brand.ink,
    contrastText: textTokens.inverse,
  },

  /** 상단바·모달 헤더 전용 — CTA와 구분되도록 크롬만 사용 */
  chrome: {
    main: brand.chrome,
    contrastText: textTokens.inverse,
  },

  gradient: {
    primary: {
      from: brand.ink,
      to: brand.ink,
      text: textTokens.inverse,
    },
    secondary: {
      from: brand.primaryDark,
      to: brand.primary,
      text: textTokens.inverse,
    },
  },

  background: {
    default: surface.canvas,
    paper: surface.default,
    dark: surface.default,
    subtle: surface.subtle,
    selected: surface.selected,
    warm: surface.warm,
  },

  text: {
    primary: textTokens.primary,
    secondary: textTokens.secondary,
    muted: textTokens.muted,
    disabled: textTokens.disabled,
    inverse: textTokens.inverse,
  },

  error: {
    main: semantic.error,
    light: semantic.errorSoft,
    dark: '#943B37',
    soft: semantic.errorSoft,
    contrastText: textTokens.inverse,
  },

  success: {
    main: semantic.success,
    light: semantic.successSoft,
    dark: '#3F622F',
    soft: semantic.successSoft,
    contrastText: textTokens.inverse,
  },

  warning: {
    main: semantic.warning,
    light: semantic.warningSoft,
    dark: '#805016',
    soft: semantic.warningSoft,
    contrastText: textTokens.inverse,
  },

  info: {
    main: semantic.info,
    light: semantic.infoSoft,
    dark: '#315F73',
    soft: semantic.infoSoft,
    contrastText: textTokens.inverse,
  },

  grey: {
    50: '#F9FAFB',
    100: surface.subtle,
    200: borderTokens.subtle,
    300: borderTokens.default,
    400: '#9CA3AF',
    500: textTokens.muted,
    600: textTokens.secondary,
    700: '#374151',
    800: textTokens.primary,
    900: brand.ink,
  },

  common: {
    black: '#000000',
    white: '#FFFFFF',
  },

  divider: borderTokens.subtle,
  border: borderTokens.default,
  borderStrong: borderTokens.strong,
  borderFocus: borderTokens.focus,
  backdrop: 'rgba(36, 48, 46, 0.45)',
};

export default colors;
